import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Seo from '../components/Seo/Seo';

interface PropertyMeta {
  title: string;
  description: string;
  json_ld: object;
}

const API_BASE = process.env.REACT_APP_USER_SERVICE_API_ENDPOINT || '';

/**
 * Dynamic SEO for public property pages.
 *
 * Fetches ready-made title, description and LodgingBusiness JSON-LD from the
 * backend (host-service /public/seo/property/<id>/) so every property page has
 * unique metadata targeting "<PG name> <locality>" searches, instead of one
 * generic "Property Details" title shared by all listings.
 *
 * Rendered alongside PropertyDetails; being deeper in the tree, its Helmet
 * values override the generic route-level fallback.
 */
const PropertySeo: React.FC = () => {
  const { property_id } = useParams<{ property_id: string }>();
  const [meta, setMeta] = useState<PropertyMeta | null>(null);

  useEffect(() => {
    if (!property_id) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/hosts/public/seo/property/${property_id}/`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d && d.title) setMeta(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [property_id]);

  if (!meta) {
    // Fallback while loading or if the property is not public
    return (
      <Seo
        title="Property Details"
        description="Book this PG bed for a short stay. All-inclusive with 3 meals a day. Pay securely via UPI or card."
        path={`/property/${property_id ?? ''}`}
      />
    );
  }

  return (
    <Seo
      title={meta.title}
      description={meta.description}
      path={`/property/${property_id}`}
      jsonLd={meta.json_ld ? [meta.json_ld] : undefined}
    />
  );
};

export default PropertySeo;
